import React from "react";
import styled from "styled-components";
import { useSelector, useStore } from "react-redux";
import i18n from "i18next";
import {
  accountLinkingService,
  currentPortfolioSelector,
  getExchangeRate,
  shortFormatNumberWithCurrency,
  tickerSubTypes,
  formatNumberWithCurrency,
  portfoliosSelector,
  isCryptoLinkingService
} from "@kubera/common";
import LinkedAccountIcon from "assets/images/linked_account_icon.svg?react";
import VerifiedIcon from "assets/images/linked_account_verified.svg?react";
import EquityBadge from "./EquityBadge";
import OpenPortfolioIcon from "assets/images/open_link.svg?react";
import CruiseControlIcon from "assets/images/cruise_control.svg?react";
import DashboardComponentExports from "components/dashboard/DashboardComponentExports";

const LinkedIcon = styled(({ isBesideText, showDescription, ...props }) => <LinkedAccountIcon {...props} />)`
  path {
    fill: ${props => props.theme.textCellLinkedHoldingsCount};
  }
  min-width: 12px;
  width: 12px;
  height: 12px;
  margin-left: 5px;
  margin-right: ${({ isBesideText }) => (isBesideText ? "0" : "5px")};
  transform: translateY(2px);
`;

const LinkDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 5px;
`;

const LinkDisplays = `
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  border-radius: 4px;
  height: 29px;
  box-sizing: border-box;
`;

const AvailableCreditContainer = styled.div`
  ${LinkDisplays}
  display: flex;
  justify-content: center;
  flex-direction: column;
  background: ${props => props.theme.linkedIconEquityContainerBG};
  font-weight: 500;
  font-size: 11px;
  line-height: 13px;
  color: ${props => props.theme.linkedIconEquityContainerCLR};
  text-transform: uppercase;
  padding: 0 3px;
  white-space: nowrap;
`;

const UnfundedCapital = styled(AvailableCreditContainer)`
  white-space: pre-line;
  margin-left: 0px;
`;

const BadgeLabel = styled.div`
  font-size: 9px;
`;

const AvailableCreditText = styled.div`
  font-weight: 500;
  font-size: 9px;
  line-height: 11px;
  letter-spacing: -0.02em;
`;

const OpenPortfolioLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border: 1px solid transparent;
  border-radius: 12px;
`;

const PortfolioLinkContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 44px;
  height: 30px;
  margin-left: -14px;
  cursor: pointer;

  &:hover ${OpenPortfolioLink} {
    background-color: ${props => props.theme.borderColorHelpMenuButton};
    border: ${props => `1px solid ${props.theme.textCellLinkedHoldingsCount}`};
    margin-left: 0px;
  }
`;

const OpenPortfolio = styled(OpenPortfolioIcon)`
  path {
    fill: ${props => props.theme.textCellLinkedHoldingsCount};
  }
  height: 9px;
  width: 9px;
`;

const AutopilotIcon = styled(CruiseControlIcon)`
  path {
    fill: ${props => props.theme.textCellLinkedHoldingsCount};
  }
  width: 16px;
  height: 14px;
`;

const LinkedIconComponent = props => {
  const store = useStore();

  const allPortfolios = useSelector(portfoliosSelector);
  const portfolio = useSelector(currentPortfolioSelector);
  const applicableAvailableCreditValue =
    props.type === accountLinkingService.IN_HOUSE_OAUTH
      ? props.availableCredit
      : props.availableCredit * getExchangeRate(store.getState(), props.currency, portfolio.currency);

  if (props.type === accountLinkingService.KUBERA_PORTFOLIO) {
    return (
      <LinkDisplayContainer>
        <LinkedIcon showDescription={props.showDescription} title={props.title} />
        {allPortfolios.find(item => item.id === props.linkProviderAccountId) && (
          <PortfolioLinkContainer
            onClick={() => DashboardComponentExports.openPortfolio(props.linkProviderAccountId)}
            title={props.title}
          >
            <OpenPortfolioLink>
              <OpenPortfolio />
            </OpenPortfolioLink>
          </PortfolioLinkContainer>
        )}
      </LinkDisplayContainer>
    );
  }

  const realEstateEquityBadge = props.isRealEstateManualAsset && !props.isBesideText && (
    <EquityBadge
      relatedId={props.relatedId}
      currency={props.currency}
      currencyCellValue={props.currencyCellValue}
      showLTV={true}
    />
  );

  if (props.hasAutopilotRulesEnabled) {
    return (
      <LinkDisplayContainer>
        <AutopilotIcon title={i18n.t("autopilot.enabled")} />
        {realEstateEquityBadge}
      </LinkDisplayContainer>
    );
  }

  if (props.isRealEstateManualAsset) {
    return realEstateEquityBadge || null;
  }

  const isHomes = [tickerSubTypes.HOME, tickerSubTypes.PRIMARY_RESIDENCE, tickerSubTypes.CARS].includes(props.subType);
  const isLinkedRowWithEquity =
    [accountLinkingService.ZILLOW, accountLinkingService.CARS, accountLinkingService.AI_APPRAISER].includes(
      props.type
    ) || isCryptoLinkingService(props.type);
  if (isHomes || isLinkedRowWithEquity) {
    return (
      <>
        {props.isBesideText && isLinkedRowWithEquity && (
          <LinkedIcon showDescription={props.showDescription} title={props.title} />
        )}
        {!props.isBesideText && (
          <EquityBadge
            relatedId={props.relatedId}
            currency={props.currency}
            currencyCellValue={props.currencyCellValue}
            showLTV={props.subType === tickerSubTypes.HOME || props.subType === tickerSubTypes.PRIMARY_RESIDENCE}
          />
        )}
      </>
    );
  }

  if (props.isVerified === true) {
    return (
      <LinkDisplayContainer>
        <VerifiedIcon title={props.title} />
      </LinkDisplayContainer>
    );
  }

  const isManualHoldingsLinkType = props.type === accountLinkingService.MANUAL_CONNECTED;

  if (
    props.availableCredit &&
    !isManualHoldingsLinkType &&
    (props.linkContainer === "investment" ||
      props.linkContainer === "locked" ||
      props.linkContainer === "credit" ||
      props.linkContainer === "creditCard" ||
      props.linkContainer === "loan" ||
      props.type === accountLinkingService.IN_HOUSE_OAUTH)
  ) {
    return (
      <LinkDisplayContainer>
        <AvailableCreditContainer>
          {props.type === accountLinkingService.IN_HOUSE_OAUTH
            ? props.availableCredit
            : shortFormatNumberWithCurrency(
                store.getState(),
                applicableAvailableCreditValue,
                portfolio.currency,
                false,
                true
              )}
          <br />
          <AvailableCreditText>
            {props.linkContainer === "investment" ||
            props.linkContainer === "locked" ||
            props.type === accountLinkingService.IN_HOUSE_OAUTH
              ? i18n.t("unvested")
              : props.linkContainer === "credit" ||
                props.linkContainer === "loan" ||
                props.linkContainer === "creditCard"
              ? i18n.t("available")
              : null}
          </AvailableCreditText>
        </AvailableCreditContainer>
      </LinkDisplayContainer>
    );
  }

  if (props.isBesideText && !isManualHoldingsLinkType) {
    return <LinkedIcon isBesideText showDescription={props.showDescription} title={props.title} />;
  }

  if (props.unfundedCapital) {
    return (
      <LinkDisplayContainer>
        <UnfundedCapital>
          {`${formatNumberWithCurrency(store.getState(), Math.kuberaFloor(props.unfundedCapital), portfolio.currency)}`}
          <BadgeLabel>{i18n.t("unfunded")}</BadgeLabel>
        </UnfundedCapital>
      </LinkDisplayContainer>
    );
  }

  return null;
};

export default LinkedIconComponent;
